import React, { useEffect, useRef, useState } from 'react';
import {
    useNavigate,
    createSearchParams,
    useSearchParams
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ID, PATH } from '../../../constants';
import { ReactComponent as Icon } from '../../../img/search.svg';
import useSearch from '../../Hooks/useSearch';
import Autocomplete from '../Autocomplete/Autocomplete';

const Search = ({
    setSearchPhraseForParent,
    hasThePhraseBeenUpdated,
    triggerAutocompleteHiding,
    isFetchingFromSearchResult
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [searchPhrase, setSearchPhrase] = useState(
        searchParams.get('phrase') ?? ''
    );
    const { isTyping, searchConfidence, results, isFetchingData, onSearch, error } = useSearch({
        searchPhrase: searchPhrase,
        isFetchingFromSearchResult: isFetchingFromSearchResult
    });
    const formEl = useRef();

    useEffect(() => {
        if (setSearchPhraseForParent) {
            setSearchPhraseForParent(searchPhrase);
        }

        onSearch(searchPhrase);
    }, [searchPhrase, onSearch, setSearchPhraseForParent]);

    useEffect(() => {
        setSearchPhrase(searchParams.get('phrase') ?? '');
    }, [searchParams]);

    const submitHandler = (e) => {
        if (!e) {
            return;
        }
        e.preventDefault();

        if (!searchPhrase) {
            return;
        }

        navigate({
            pathname: `${PATH.helpAndSupport}/search`,
            search: `?${createSearchParams({ phrase: searchPhrase })}`
        });
    };

    const shouldDisplayAutocomplete = hasThePhraseBeenUpdated
        ? hasThePhraseBeenUpdated() && isTyping
        : isTyping;

    const submitButton = (
        <button
            type="button"
            className="button btn-cta help-form__btn"
            data-cy="search-bar-button"
            onClick={submitHandler}
        >
            <Icon className="icon icon--s help-form__icon" aria-hidden="true" />
            <span className="help-form__btn-text">{t('ui.search.button')}</span>
        </button>
    );

    return (
        <form className="help-form" role="search" onSubmit={submitHandler}>
            <div ref={formEl} className="help-form__wrapper">
                <label className="sr-only" htmlFor={ID.searchInputId}>{t('ui.search.label')}</label>
                <input
                    id={ID.searchInputId}
                    className="input help-form__input"
                    type="text"
                    placeholder={t('ui.search.placeholder')}
                    value={searchPhrase}
                    onChange={(e) => setSearchPhrase(e.target.value)}
                    data-cy="search-bar"
                    data-nr="search-bar"
                    aria-label={t('ui.search.label')}
                />
                <Autocomplete
                    key={shouldDisplayAutocomplete}
                    parentRef={formEl}
                    searchConfidence={searchConfidence}
                    searchPhrase={searchPhrase}
                    submitHandler={submitHandler}
                    results={results}
                    isFetchingData={isFetchingData}
                    isVisible={shouldDisplayAutocomplete}
                    triggerAutocompleteHiding={triggerAutocompleteHiding}
                    error={error}
                />
            </div>
            {submitButton}
        </form>
    );
};

export default Search;
